import axios, { AxiosResponse } from "axios";
import moment from "moment";
import Bottleneck from "bottleneck";
import { workforceShiftReportYmd } from "utils/revenueReportDates";

interface Department {
  id: number;
  name: string;
  location_id?: number;
  /** Some API responses use camelCase */
  locationId?: number;
  // …other fields
}

/** Workforce department location key (snake_case or camelCase from API). */
export function getDepartmentLocationId(
  d: Department | undefined,
): number | null {
  if (!d) return null;
  const any = d as unknown as Record<string, unknown>;
  const v =
    (any.location_id as number | undefined) ??
    (any.locationId as number | undefined);
  return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
}

/** Departments counted in API but omitted from Workforce location daily hour totals. */
const DEFAULT_EXCLUDED_DEPARTMENT_NAMES_BY_RC: Record<string, string[]> = {
  "7002": ["7002|Houston Galleria Valet|04|Valet"],
};

/**
 * Optional override: `REACT_APP_WORKFORCE_EXCLUDED_DEPARTMENT_NAMES_BY_RC='{"7002":["7002|Houston Galleria Valet|04|Valet"]}'`
 */
function excludedDepartmentNamesForRc(rc: string): Set<string> | undefined {
  const raw = process.env.REACT_APP_WORKFORCE_EXCLUDED_DEPARTMENT_NAMES_BY_RC;
  let names = DEFAULT_EXCLUDED_DEPARTMENT_NAMES_BY_RC[rc] ??
    DEFAULT_EXCLUDED_DEPARTMENT_NAMES_BY_RC[String(Number(rc))];
  if (raw?.trim()) {
    try {
      const obj = JSON.parse(raw) as Record<string, string[]>;
      const fromEnv = obj[rc] ?? obj[String(Number(rc))];
      if (fromEnv?.length) names = fromEnv;
    } catch {
      /* keep defaults */
    }
  }
  if (!names?.length) return undefined;
  return new Set(names);
}

function isDepartmentExcludedFromHours(
  departmentName: string | undefined,
  locationRc: string | number,
): boolean {
  const excluded = excludedDepartmentNamesForRc(String(locationRc).trim());
  if (!excluded || !departmentName) return false;
  return excluded.has(departmentName);
}

/**
 * Optional override: `REACT_APP_WORKFORCE_EXTRA_DEPARTMENTS_BY_RC='{"5042":[12,34]}'`
 * Include shift department_ids that never match by name/location (misconfigured Workforce).
 */
function extraDepartmentIdsForRc(rc: string): Set<number> | undefined {
  const raw = process.env.REACT_APP_WORKFORCE_EXTRA_DEPARTMENTS_BY_RC;
  if (!raw?.trim()) return undefined;
  try {
    const obj = JSON.parse(raw) as Record<string, number[]>;
    const ids = obj[rc] ?? obj[String(Number(rc))];
    if (!ids?.length) return undefined;
    return new Set(ids.map(Number));
  } catch {
    return undefined;
  }
}

export interface Shift {
  id: number;
  department_id: number;
  user_id?: number;
  /** Payroll calendar day from Workforce (YYYY-MM-DD); use for daily totals. */
  date?: string;
  start: number; // epoch seconds
  finish: number; // epoch seconds
  break_start?: number | null;
  break_finish?: number | null;
  break_length?: number | null; // minutes
  // …other fields
}

/**
 * Paid hours: clock span minus recorded break (break_length or break start/finish).
 * Matches Workforce export; does not cap or auto-deduct meal breaks.
 */
export function computeWorkedHoursFromShiftData(shift: Shift): number {
  if (shift.start == null || shift.finish == null) return 0;

  const startM = moment.unix(shift.start);
  const finishM = moment.unix(shift.finish);
  let totalMinutes = moment.duration(finishM.diff(startM)).asMinutes();

  if (shift.break_length != null && shift.break_length > 0) {
    totalMinutes -= shift.break_length;
  } else if (shift.break_start != null && shift.break_finish != null) {
    totalMinutes -= moment
      .unix(shift.break_finish)
      .diff(moment.unix(shift.break_start), "minutes");
  }

  return Math.max(0, totalMinutes) / 60;
}

/**
 * Match a location RC to a Workforce department name without substring bugs
 * (e.g. RC "8" must not match department "...1008...").
 */
export function departmentNameMatchesLocationRc(
  departmentName: string | undefined,
  locationRc: string | number | undefined | null,
): boolean {
  if (!departmentName || locationRc == null || locationRc === "") return false;
  const rc = String(locationRc).trim();
  if (!rc) return false;
  const numericChunks = departmentName.match(/\d+/g);
  if (!numericChunks) return false;
  return numericChunks.includes(rc);
}

/**
 * Shifts for a Firebase location RC: departments whose **name** contains the RC token,
 * plus any other department with the same Workforce {@link Department.location_id}
 * (same site often has multiple departments; only some names include "5042").
 */
export function shiftsMatchingLocationRc(
  allShifts: ShiftWithDept[],
  locationRc: string | number,
): ShiftWithDept[] {
  const rcKey = String(locationRc).trim();
  const extraDeptIds = extraDepartmentIdsForRc(rcKey);

  const seedLocationIds = new Set<number>();
  allShifts.forEach((s) => {
    if (!departmentNameMatchesLocationRc(s.departmentData?.name, locationRc)) {
      return;
    }
    const lid = getDepartmentLocationId(s.departmentData);
    if (lid != null) {
      seedLocationIds.add(lid);
    }
  });

  return allShifts.filter((shift) => {
    if (
      isDepartmentExcludedFromHours(shift.departmentData?.name, locationRc)
    ) {
      return false;
    }
    if (extraDeptIds?.has(shift.department_id)) {
      return true;
    }
    if (
      departmentNameMatchesLocationRc(shift.departmentData?.name, locationRc)
    ) {
      return true;
    }
    const lid = getDepartmentLocationId(shift.departmentData);
    return lid != null && seedLocationIds.has(lid);
  });
}

/** DevTools: call after changing filters if totals look stale (30‑minute cache). */
export function clearWorkforceShiftsCache(): void {
  shiftsCache.clear();
}

interface User {
  id: number;
  name: string;
  // …other fields
}

export interface ShiftWithDept {
  department_id: number;
  startFinishDifferenceHours: string;
  departmentData: Department;
  shiftData: Shift;
  userName?: string;
  workedHours?: number;
}

/** Hours for a shift row returned from {@link getShiftsWithDepartments}. */
export function getShiftWorkedHours(s: ShiftWithDept): number {
  return computeWorkedHoursFromShiftData(s.shiftData);
}

/**
 * Unique key per Workforce shift row. Do not dedupe on start/finish alone — many
 * employees at the same site share identical clock times.
 */
export function workforceShiftDedupeKey(s: ShiftWithDept): string {
  const shift = s.shiftData;
  if (shift?.id != null) return `shift:${shift.id}`;
  const userId = shift?.user_id;
  return `${userId ?? ""}-${s.department_id}-${shift?.start ?? ""}-${shift?.finish ?? ""}`;
}

const API_BASE = "https://my.workforce.com/api/v2";
const TOKEN = process.env.REACT_APP_WORKFORCE_TOKEN!;

/** Workforce hours integration; disable per-env with REACT_APP_ENABLE_WORKFORCE=false. */
export const WORKFORCE_ENABLED =
  process.env.REACT_APP_ENABLE_WORKFORCE !== "false";

const MAX_CONCURRENT = 10;
const RESERVOIR = 200;
const WINDOW_MS = 60 * 1000;

/** Shift pages fetched concurrently per batch (page_size is capped at 100 by the API). */
const PARALLEL_SHIFT_PAGES = 10;

const SHIFTS_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
const shiftsCache = new Map<
  string,
  { result: ShiftWithDept[]; expires: number }
>();
const departmentCache = new Map<number, Department>();
const userCache = new Map<number, User>();

const limiter = new Bottleneck({
  reservoir: RESERVOIR,
  reservoirRefreshAmount: RESERVOIR,
  reservoirRefreshInterval: WINDOW_MS,
  maxConcurrent: MAX_CONCURRENT,
});

async function requestWithRetry<T>(
  fn: () => Promise<AxiosResponse<T>>,
): Promise<T> {
  try {
    const resp = await limiter.schedule(fn);
    return resp.data;
  } catch (err: any) {
    if (err.response?.status === 429) {
      const resetSec = parseInt(err.response.headers["x-ratelimit-reset"], 10);
      const waitMs = resetSec * 1000 - Date.now() + 500;
      if (waitMs > 0) await new Promise((r) => setTimeout(r, waitMs));
      const retryResp = await limiter.schedule(fn);
      return retryResp.data;
    }
    throw err;
  }
}

async function directRequest<T>(
  fn: () => Promise<AxiosResponse<T>>,
): Promise<T> {
  const resp = await fn();
  return resp.data;
}

/**
 * Fetch shifts + departments, optionally using rate‐limiting.
 * Results are cached by (from, to) for 30 minutes; department lookups are cached for the session.
 *
 * @param from         YYYY-MM-DD
 * @param to           YYYY-MM-DD
 * @param useRateLimit default=true
 */
export async function getShiftsWithDepartments(
  from: string,
  to: string,
  useRateLimit = true,
): Promise<ShiftWithDept[]> {
  const cacheKey = `${from}:${to}`;
  const cached = shiftsCache.get(cacheKey);
  if (cached && cached.expires > Date.now()) {
    return cached.result;
  }

  const headers = {
    Authorization: `Bearer ${TOKEN}`,
    Accept: "application/json",
  };

  const doRequest = useRateLimit ? requestWithRetry : directRequest;

  // API is paginated (page_size max 100). Fetch page 1 first to learn whether more
  // pages exist, then fetch the rest in parallel batches instead of one-at-a-time —
  // a full month of shifts can be 20–50 pages, so sequential fetching is the main
  // source of the multi-minute wait. A page shorter than PAGE_SIZE marks the end.
  const PAGE_SIZE = 100;
  const fetchShiftPage = (page: number) =>
    doRequest<Shift[]>(() =>
      axios.get(`${API_BASE}/shifts`, {
        headers,
        params: { from, to, page_size: PAGE_SIZE, page },
      }),
    );

  const allShifts: Shift[] = [];

  const firstChunk = await fetchShiftPage(1);
  allShifts.push(...firstChunk);

  if (firstChunk.length === PAGE_SIZE) {
    let nextPage = 2;
    let reachedEnd = false;
    while (!reachedEnd) {
      const batchStart = nextPage;
      const pageNumbers = Array.from(
        { length: PARALLEL_SHIFT_PAGES },
        (_, i) => batchStart + i,
      );
      const chunks = await Promise.all(pageNumbers.map(fetchShiftPage));
      for (const chunk of chunks) {
        allShifts.push(...chunk);
        if (chunk.length < PAGE_SIZE) {
          reachedEnd = true;
        }
      }
      nextPage += PARALLEL_SHIFT_PAGES;
    }
  }

  const validShifts = allShifts.filter((s) => s.department_id != null);

  const uniqueDeptIds = Array.from(
    new Set(validShifts.map((s) => s.department_id)),
  );

  const idsToFetch = uniqueDeptIds.filter((id) => !departmentCache.has(id));
  const rawDeptEntries = await Promise.all(
    idsToFetch.map(async (id) => {
      try {
        const dept = await doRequest<Department>(() =>
          axios.get(`${API_BASE}/departments/${id}`, { headers }),
        );
        departmentCache.set(id, dept);
        return [id, dept] as [number, Department];
      } catch (err: any) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          console.warn(`Department ${id} not found (404), skipping it.`);
          return null;
        }
        throw err;
      }
    }),
  );

  rawDeptEntries.forEach((e) => {
    if (e) departmentCache.set(e[0], e[1]);
  });

  const deptCache = new Map<number, Department>();
  uniqueDeptIds.forEach((id) => {
    const d = departmentCache.get(id);
    if (d) deptCache.set(id, d);
  });

  // Resolve user names (GET /users/{id}) for unique user_ids on shifts; cache for session.
  const uniqueUserIds = Array.from(
    new Set(
      validShifts
        .map((s) => (s as any).user_id)
        .filter((id): id is number => id != null),
    ),
  );
  const userIdsToFetch = uniqueUserIds.filter((id) => !userCache.has(id));
  await Promise.all(
    userIdsToFetch.map(async (id) => {
      try {
        const user = await doRequest<User>(() =>
          axios.get(`${API_BASE}/users/${id}`, { headers }),
        );
        userCache.set(id, user);
      } catch (err: any) {
        if (axios.isAxiosError(err) && err.response?.status === 404) {
          console.warn(`User ${id} not found (404), skipping name.`);
        } else {
          console.warn(`Failed to fetch user ${id}:`, err?.message);
        }
      }
    }),
  );

  const result = validShifts.map((shift) => {
    const workedHours = computeWorkedHoursFromShiftData(shift);
    const workedMinutes = workedHours * 60;
    const hrs = Math.floor(workedHours);
    const mins = Math.round(workedMinutes % 60);
    const readable =
      [hrs > 0 ? `${hrs}h` : null, mins > 0 ? `${mins}m` : null]
        .filter(Boolean)
        .join(" ") || "0m";
    const userId = (shift as any).user_id;
    const userName = userId != null ? userCache.get(userId)?.name : undefined;

    return {
      department_id: shift.department_id,
      startFinishDifferenceHours: readable,
      departmentData: deptCache.get(shift.department_id)!,
      shiftData: shift,
      userName,
      workedHours,
    };
  });

  shiftsCache.set(cacheKey, {
    result,
    expires: Date.now() + SHIFTS_CACHE_TTL_MS,
  });
  return result;
}

function shiftWorkedHours(s: ShiftWithDept): number {
  return getShiftWorkedHours(s);
}

export interface WorkforceHoursEntry {
  locationName: string;
  locationRc: string;
  shifts: ShiftWithDept[];
}

/** Console: one object with all daily shifts (deduped) — same range as Daily report. */
export function logWorkforceDailyHours(opts: {
  range: { startYmd: string; endYmd: string };
  selectedLabel: string;
  entries: WorkforceHoursEntry[];
}): void {
  const { range, selectedLabel, entries } = opts;
  const inRange = (s: ShiftWithDept) => {
    const ymd = workforceShiftReportYmd(s);
    return ymd && ymd >= range.startYmd && ymd <= range.endYmd;
  };
  const seen = new Set<string>();

  const shifts: Array<{
    date: string;
    name: string;
    hrs: number;
    location: string;
    /** Workforce `GET /departments/{id}`. */
    departmentId: number;
    /** Workforce department `location_id` / `locationId` (same site); null if API omitted it. */
    workforceLocationId: number | null;
    department: string | undefined;
  }> = [];
  let totalHrs = 0;
  const distinctWorkforceLocationIds = new Set<number>();
  const distinctDepartmentIds = new Set<number>();

  entries.forEach((entry) => {
    const loc = `${entry.locationName} (RC ${entry.locationRc})`;
    entry.shifts.forEach((s) => {
      if (!inRange(s)) return;
      const dk = workforceShiftDedupeKey(s);
      if (seen.has(dk)) return;
      seen.add(dk);
      const hrs = Math.round(shiftWorkedHours(s) * 100) / 100;
      totalHrs += hrs;
      const workforceLocationId = getDepartmentLocationId(s.departmentData);
      if (workforceLocationId != null) {
        distinctWorkforceLocationIds.add(workforceLocationId);
      }
      distinctDepartmentIds.add(s.department_id);
      shifts.push({
        date: workforceShiftReportYmd(s),
        name: s.userName ?? "(unknown)",
        hrs,
        location: loc,
        departmentId: s.department_id,
        workforceLocationId,
        department: s.departmentData?.name,
      });
    });
  });

  // Single console payload (one object) for easy copy / expand in DevTools
  console.log({
    source: "Workforce Daily hrs",
    startYmd: range.startYmd,
    endYmd: range.endYmd,
    selectedLocations: selectedLabel,
    departmentIds: Array.from(distinctDepartmentIds).sort((a, b) => a - b),
    workforceLocationIds: Array.from(distinctWorkforceLocationIds).sort(
      (a, b) => a - b
    ),
    totalHrs: totalHrs.toFixed(2),
    shifts,
  });
}
